<template>
  <div class="subscription-change-container">
    <b-loading :is-full-page="true" v-model="loading"></b-loading>
    <!-- Header Section -->
    <div class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">Manage Your Subscription</h1>
        <p class="hero-subtitle">Update your plan or features anytime</p>
      </div>
    </div>

    <div v-if="subscription" class="main-content">
      <!-- Plan Selection Section -->
      <div class="section-card feature-card">
        <div class="section-header">
          <h2 class="section-title">
            <i class="fas fa-th-large"></i>
            Basic Plans
          </h2>
          <p class="section-description">Select your base inquiry plan</p>
        </div>

        <div class="plans-grid">
          <div v-for="plan in filteredOptions" :key="plan.id" class="plan-card-wrapper">
            <SubscriptionOption
              :is-active="subscription.plan_id == plan.id"
              :is-selected="selectedPlanId === plan.id"
              :plan="plan"
              @click="selectPlan"
              class="enhanced-plan-option"
            />
          </div>
        </div>
      </div>

      <!-- Blanket VOB Feature Section -->
      <div class="section-card feature-card" data-feature="blanket-vob">
        <div class="section-header">
          <h2 class="section-title">
            <i class="fas fa-search-plus"></i>
            Blanket VOB Search
          </h2>
          <a @click="showBlanketVOBModal = !showBlanketVOBModal" class="feature-link">
            <i class="fas fa-info-circle"></i>
            See details
          </a>
        </div>

        <div class="feature-content">
          <div class="feature-toggle">
            <b-switch
              size="is-large"
              @input="handleBlanketVOBToggle($event)"
              v-model="hasBlanketVOB"
              type="is-success"
            >
              <span class="toggle-label">Enable Blanket VOB Search</span>
            </b-switch>
          </div>

          <!-- Blanket VOB Plan Selection -->
          <div v-if="hasBlanketVOB" class="blanket-vob-section">
            <div class="blanket-plans-grid">
              <div
                v-for="plan in blanketVOBOptions"
                :key="plan.id"
                class="blanket-plan-card"
                :class="{
                  'is-selected': selectedBlanketVOBPlanId === plan.id,
                  'is-active':
                    subscriptionHasBlanketVOBWithoutTrial &&
                    subscription.blanket_vob_plan_id === plan.id,
                }"
                @click="selectBlanketVOBPlan(plan.id)"
              >
                <div class="card-content">
                  <div class="media">
                    <div class="media-content has-text-centered">
                      <p class="title is-4">{{ plan.name }}</p>
                      <p class="subtitle is-6">${{ plan.base_cost }}/mo</p>
                    </div>
                  </div>

                  <div class="content">
                    <div class="mt-2 has-text-centered">
                      <span
                        v-if="
                          subscription.has_blanket_vob &&
                          subscription.blanket_vob_plan_id === plan.id
                        "
                        class="has-text-success"
                      >
                        <i class="fas fa-check-circle"></i>
                      </span>
                      <span
                        v-else-if="selectedBlanketVOBPlanId === plan.id"
                        class="has-text-primary"
                      >
                        <i class="fas fa-dot-circle"></i>
                      </span>
                    </div>

                    <ul>
                      <li>
                        {{ plan.base_inquiries }} Inquiries
                        <small class="has-text-grey">(${{ plan.addl_cost }} per extra)</small>
                      </li>
                      <li>Unlimited Users</li>
                      <li>Unlimited Providers/Facilities</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reimbursement Feature Section -->
      <div class="section-card feature-card">
        <div class="section-header">
          <h2 class="section-title">
            <i class="fas fa-chart-line"></i>
            Reimbursement Estimates
          </h2>
          <a @click="showModal = !showModal" class="feature-link">
            <i class="fas fa-info-circle"></i>
            See details
          </a>
        </div>

        <div class="feature-content">
          <div class="feature-text">
            <p>Access historical payer data for informed reimbursement decisions.</p>
            <div class="feature-actions">
              <a
                v-if="!hasFeatureReimbursement"
                @click="showCoupon = !showCoupon"
                class="feature-link"
              >
                <i class="fas fa-tag"></i>
                Apply Coupon
              </a>
            </div>
          </div>

          <div class="feature-toggle">
            <b-switch
              size="is-large"
              @input="handleFeature($event)"
              v-model="hasFeatureReimbursement"
              type="is-info"
            >
              <span class="toggle-label">Enable Reimbursement Estimates ($500/month)</span>
            </b-switch>
          </div>

          <!-- Coupon Section -->
          <div v-if="showCoupon" class="coupon-section">
            <div class="coupon-card">
              <div class="coupon-header">
                <i class="fas fa-ticket-alt"></i>
                <h4>Apply Coupon Code</h4>
              </div>
              <b-field :type="error ? 'is-danger' : ''" :message="error">
                <b-input
                  v-model="promotion"
                  type="text"
                  placeholder="Enter your coupon code"
                  @keyup.enter="handleFeature(true)"
                  @input="clearError"
                  size="is-medium"
                  icon="tag"
                ></b-input>
              </b-field>
            </div>
          </div>

          <!-- Error Display -->
          <b-notification
            v-if="error"
            type="is-danger"
            role="alert"
            class="error-notification"
            :closable="false"
          >
            <i class="fas fa-exclamation-triangle"></i>
            {{ error }}
          </b-notification>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-section">
        <div class="action-buttons">
          <b-tooltip
            :label="updateButtonTooltip"
            :active="!hasChanges && updateButtonTooltip !== ''"
            position="is-top"
            type="is-dark"
            multilined
          >
            <b-button
              type="is-primary"
              size="is-large"
              @click="updatePlan"
              :disabled="!hasChanges"
              class="update-button"
            >
              <i class="fas fa-rocket"></i>
              <span>Update Plan</span>
            </b-button>
          </b-tooltip>

          <b-button
            type="is-danger"
            size="is-medium"
            @click="onManageSubscription"
            outlined
            class="cancel-button"
          >
            <i class="fas fa-times-circle"></i>
            <span>Cancel Subscription</span>
          </b-button>
        </div>
      </div>
    </div>

    <b-modal v-model="showModal" :width="640" scroll="keep">
      <reimbursement-feature-detail></reimbursement-feature-detail>
    </b-modal>

    <b-modal v-model="showBlanketVOBModal" :width="640" scroll="keep">
      <BlanketVOBFeatureDetail></BlanketVOBFeatureDetail>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import request from '../request';
import SubscriptionOption from './SubscriptionOption';
import ReimbursementFeatureDetail from './ReimbursementFeatureDetail';
import BlanketVOBFeatureDetail from './BlanketVOBFeatureDetail';

export default {
  name: 'SubscriptionChange',
  components: {
    SubscriptionOption,
    ReimbursementFeatureDetail,
    BlanketVOBFeatureDetail,
  },
  data() {
    return {
      loading: false,
      options: [],
      blanketVOBOptions: [],
      subscription: null,
      showModal: false,
      showBlanketVOBModal: false,
      showCoupon: false,
      promotion: '',
      error: '',
      hasBlanketVOB: false,
      subscriptionHasBlanketVOBWithoutTrial: false,
      hasFeatureReimbursement: false,
      selectedPlanId: null,
      selectedBlanketVOBPlanId: null,
    };
  },
  computed: {
    ...mapGetters(['organization']),
    activePlan() {
      return this.options.find((opt) => this.subscription.plan_id === opt.id);
    },
    filteredOptions() {
      return this.options
        .filter((opt) => {
          // Show enabled as well as the activesubscription if its grandfathered.
          return opt.is_enabled || opt.id === this.subscription.plan_id;
        })
        .sort((a, b) => {
          // Sort so tiers are shown small to large.
          return a.tier > b.tier;
        });
    },
    hasChanges() {
      if (!this.subscription) return false;

      // Check if basic plan has changed
      const planChanged = this.selectedPlanId && this.selectedPlanId !== this.subscription.plan_id;

      // // Check if blanket VOB state has changed
      const blanketVOBChanged = this.hasBlanketVOB !== this.subscriptionHasBlanketVOBWithoutTrial;

      // Check if blanket VOB plan has changed
      const blanketVOBPlanChanged =
        this.selectedBlanketVOBPlanId &&
        this.selectedBlanketVOBPlanId !== this.subscription.blanket_vob_plan_id;

      // Check if reimbursement feature has changed
      const reimbursementChanged =
        this.hasFeatureReimbursement !== this.subscription.has_feature_reimbursement;

      // If blanket VOB is enabled but no plan is selected, disable the button
      if (this.hasBlanketVOB && !this.selectedBlanketVOBPlanId) {
        return false;
      }

      return planChanged || blanketVOBChanged || blanketVOBPlanChanged || reimbursementChanged;
    },
    updateButtonTooltip() {
      if (!this.subscription) return '';

      // If blanket VOB is enabled but no plan is selected
      if (this.hasBlanketVOB && !this.selectedBlanketVOBPlanId) {
        return 'Please select a Blanket VOB plan to continue';
      }

      // If no changes have been made
      if (!this.hasChanges) {
        return 'Please make a change to update your plan';
      }

      return '';
    },
  },
  mounted() {
    this.loading = true;
    Promise.all([this.fetchCurrentSubscription(), this.fetchPlans()]).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    fetchCurrentSubscription() {
      return request.get('/subscriptions/current').then((resp) => {
        this.subscription = resp.data.subscription;
        // Initialize blanket VOB state from subscription
        if (this.subscription.has_blanket_vob) {
          if (
            this.subscription.subscription_products.some(
              (sp) =>
                sp.product_id === this.subscription.blanket_vob_plan_id &&
                sp.plan_type !== 'blanket_vob_trial' &&
                sp.cancelled_at === null
            )
          ) {
            this.hasBlanketVOB = true;
            this.selectedBlanketVOBPlanId = this.subscription.blanket_vob_plan_id;
            this.subscriptionHasBlanketVOBWithoutTrial = true;
          }
        }
        // Initialize reimbursement state from subscription
        this.hasFeatureReimbursement = this.subscription.has_feature_reimbursement || false;
      });
    },
    fetchPlans() {
      return request.get('/plans').then((resp) => {
        this.options = resp.data.plans.basic_inquiries_plans;
        this.blanketVOBOptions = resp.data.plans.blanket_vob_plans;
      });
    },
    clearError() {
      this.error = '';
    },
    onManageSubscription() {
      this.loading = true;
      request.post('/subscriptions/portal-session').then((resp) => {
        window.location.href = resp.data.redirect_url;
      });
    },
    selectPlan(plan) {
      this.selectedPlanId = plan.id;
    },
    selectBlanketVOBPlan(planId) {
      this.selectedBlanketVOBPlanId = planId;
    },
    handleBlanketVOBToggle(event) {
      this.hasBlanketVOB = event;
      // If turning off, clear selected plan
      if (!event) {
        this.selectedBlanketVOBPlanId = null;
      }
    },
    handleFeature(event) {
      this.hasFeatureReimbursement = event;
      // If turning off, hide coupon section and clear promotion
      if (!event) {
        this.showCoupon = false;
        this.promotion = '';
      }
    },
    updatePlan() {
      if (!this.hasChanges) return;

      // Validate promotion if reimbursement is being activated
      if (
        this.hasFeatureReimbursement &&
        !this.subscription.has_feature_reimbursement &&
        this.showCoupon &&
        this.promotion &&
        this.promotion.trim() === ''
      ) {
        this.error = 'Please enter a valid coupon code or close the coupon field';
        return;
      }

      // Prepare the request payload
      const payload = {};

      // Add basic plan change if selected
      if (this.selectedPlanId && this.selectedPlanId !== this.subscription.plan_id) {
        payload.new_plan_id = this.selectedPlanId;
      }

      // Add blanket VOB changes
      if (this.hasBlanketVOB !== this.subscriptionHasBlanketVOBWithoutTrial) {
        payload.has_blanket_vob = this.hasBlanketVOB;
      }

      // Add blanket VOB plan change if needed
      if (this.hasBlanketVOB) {
        // If enabling Blanket VOB, always send the selected plan id
        if (!this.subscriptionHasBlanketVOBWithoutTrial && this.selectedBlanketVOBPlanId) {
          payload.new_blanket_vob_plan_id = this.selectedBlanketVOBPlanId;
          payload.has_blanket_vob = true;
        } else if (
          this.selectedBlanketVOBPlanId &&
          this.selectedBlanketVOBPlanId !== this.subscription.blanket_vob_plan_id
        ) {
          // If changing to a different plan while already enabled
          payload.new_blanket_vob_plan_id = this.selectedBlanketVOBPlanId;
          payload.has_blanket_vob = true;
        }
      }

      // Add reimbursement changes
      if (this.hasFeatureReimbursement !== this.subscription.has_feature_reimbursement) {
        payload.has_feature_reimbursement = this.hasFeatureReimbursement;

        // Include promotion if activating and coupon section is shown and has a value
        if (
          this.hasFeatureReimbursement &&
          this.showCoupon &&
          this.promotion &&
          this.promotion.trim() !== ''
        ) {
          payload.promotion = this.promotion.trim();
        }
      }

      // Confirm with user
      const changes = [];
      if (payload.new_plan_id) {
        const newPlan = this.options.find((p) => p.id === this.selectedPlanId);
        changes.push(`Change basic plan to ${newPlan.name}`);
      }
      if (payload.has_blanket_vob !== undefined) {
        changes.push(`${payload.has_blanket_vob ? 'Activate' : 'Deactivate'} Blanket VOB Search`);
      }
      if (payload.new_blanket_vob_plan_id) {
        const newBlanketPlan = this.blanketVOBOptions.find(
          (p) => p.id === this.selectedBlanketVOBPlanId
        );
        changes.push(`Change Blanket VOB plan to ${newBlanketPlan.name}`);
      }
      if (payload.has_feature_reimbursement !== undefined) {
        changes.push(
          `${payload.has_feature_reimbursement ? 'Activate' : 'Deactivate'} Reimbursement Estimates`
        );
        if (payload.has_feature_reimbursement) {
          changes.push(
            '<small>By activating this feature you are agreeing to the QuickAdmit Terms & Conditions.</small>'
          );
        }
      }

      const msg = `<p>This will make the following changes:</p><ul>${changes.map((c) => `<li>${c}</li>`).join('')}</ul><br/><p>Your billing will be prorated and you will be billed/credited at the end of your normal billing cycle.</p>`;

      this.$buefy.dialog.confirm({
        title: 'Update Plan',
        message: msg,
        confirmText: 'Update',
        type: 'is-success',
        onConfirm: () => {
          this.loading = true;
          request
            .post('/subscriptions/change', payload)
            .then(() => {
              this.$buefy.toast.open({
                message: 'Your subscription has been updated successfully.',
                type: 'is-success',
              });
              this.error = '';
              this.showCoupon = false;
              this.promotion = ''; // Clear the promotion code after successful use

              // Redirect to organization settings page
              setTimeout(() => {
                this.$router.push({ name: 'OrganizationDetail' });
              }, 1500); // Wait 1.5 seconds to show the success message
            })
            .catch((err) => {
              if (err.response && err.response.status === 422) {
                this.error = err.response.data.errors.NOFIELD || 'Invalid request';
              } else {
                this.error = 'An error occurred while updating your subscription';
              }
            })
            .finally(() => {
              // Only handle loading if we're not redirecting (i.e., on error)
              if (this.error) {
                this.fetchCurrentSubscription().finally(() => (this.loading = false));
              } else {
                this.loading = false;
              }
            });
        },
      });
    },
  },
};
</script>

<style>
/* Override parent section padding specifically for subscription change */
.subscription-change-container {
  margin-top: -3rem !important; /* Bulma section default padding is 3rem */
}
</style>

<style scoped>
.subscription-change-container {
  min-height: 100vh;
  background: none;
  padding: 0;
  margin: 0;
  margin-top: -0.5rem;
}

.hero-section {
  background: #ffffff;
  color: #1f2937;
  padding: 0;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
  position: relative !important;
  top: -1.5rem !important;
  z-index: 10;
}

.hero-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 0.5rem 2rem;
}

.hero-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.hero-subtitle {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 400;
  margin: 0;
}

.main-content {
  background: #f8fafc;
  min-height: calc(100vh - 60px);
  padding: 0 2rem 2rem 2rem;
  margin-top: 0;
  border-radius: 0;
  position: relative;
  z-index: 1;
}

.current-plan-banner {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 1rem 2rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.current-plan-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1.1rem;
}

.current-plan-content i {
  color: #ffd700;
}

.section-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.15);
}

.feature-card {
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-title i {
  color: #667eea;
  font-size: 1.5rem;
}

.section-description {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
}

.feature-badge {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.feature-badge.professional {
  background: linear-gradient(135deg, #06b6d4, #0891b2);
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.plan-card-wrapper {
  transition: transform 0.3s ease;
}

.plan-card-wrapper:hover {
  transform: translateY(-4px);
}

.feature-content {
  display: flex;
  flex-direction: column;
  gap: 0rem;
}

.feature-text {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
}

.feature-text h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.feature-text p {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.feature-pricing {
  margin-bottom: 0.5rem;
}

.price {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  display: inline-block;
}

.feature-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.feature-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.feature-link:hover {
  color: #4f46e5;
}

.feature-toggle {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 1rem;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}

.feature-toggle:hover {
  border-color: #d1d5db;
}

.toggle-label {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1f2937;
}

.blanket-vob-section {
  background: none;
  padding: 2rem 0;
  border: none;
}

.blanket-plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.blanket-plan-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 0;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow:
    0 2px 3px rgba(10, 10, 10, 0.1),
    0 0 0 1px rgba(10, 10, 10, 0.1);
}

.blanket-plan-card:hover {
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
}

.blanket-plan-card.is-selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05));
}

.blanket-plan-card.is-active {
  border-color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(5, 150, 105, 0.05));
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.plan-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.plan-status {
  font-size: 1.25rem;
}

.current-icon {
  color: #10b981;
}

.selected-icon {
  color: #667eea;
}

.plan-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.plan-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.plan-feature i {
  color: #667eea;
  width: 1rem;
}

.coupon-section {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
}

.coupon-card {
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.coupon-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.coupon-header i {
  color: #f59e0b;
  font-size: 1.25rem;
}

.coupon-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.error-notification {
  margin-top: 1rem;
  border-left: 4px solid #ef4444;
}

.error-notification i {
  margin-right: 0.5rem;
}

.action-section {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.action-buttons {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center;
  justify-content: center;
}

.update-button {
  background: linear-gradient(135deg, #667eea, #764ba2) !important;
  border: none !important;
  border-radius: 0.75rem !important;
  padding: 1rem 2rem !important;
  font-weight: 600 !important;
  font-size: 1.1rem !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;
  flex: 1;
  max-width: 200px;
}

.update-button:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
}

.update-button:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.update-button i {
  margin-right: 0.5rem;
}

.cancel-button {
  border-radius: 0.75rem !important;
  font-weight: 400 !important;
  font-size: 1rem !important;
  padding: 0.875rem 1.5rem !important;
  transition: all 0.3s ease !important;
  opacity: 0.8 !important;
  flex: 1;
  max-width: 180px;
}

.cancel-button:hover {
  transform: translateY(-1px) !important;
  opacity: 1 !important;
}

.cancel-button i {
  margin-right: 0.5rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .main-content {
    padding: 1rem;
  }

  .section-card {
    padding: 1.5rem;
  }

  .feature-text {
    flex-direction: column;
    text-align: center;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .blanket-plans-grid {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    width: 100%;
  }

  .update-button,
  .cancel-button {
    width: 100% !important;
  }
}

/* Legacy plan option styles (keeping for compatibility) */
.plan-option {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.plan-option:hover {
  border-color: #3273dc;
  transform: translateY(-2px);
}

.plan-option.is-selected {
  border-color: #3273dc;
  background-color: #f0f6ff;
}

.plan-option.is-active {
  border-color: #23d160;
  background-color: #f0fff4;
}

.is-clickable {
  cursor: pointer;
}
</style>
